import React from 'react';

interface TimeInput12HourProps {
  value: string; // "h:mm AM/PM" format (e.g. "6:00 PM")
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  name?: string;
}

/** Parse "h:mm AM/PM" string into parts */
function parseParts(value: string): { hours: string; minutes: string; period: 'AM' | 'PM' } {
  if (!value) return { hours: '', minutes: '', period: 'AM' };
  const match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    return { hours: match[1], minutes: match[2], period: match[3].toUpperCase() as 'AM' | 'PM' };
  }
  // Fallback: try parsing as 24h "HH:mm" or "HH:mm:ss" for backward compat
  const parts = value.split(':');
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!isNaN(h) && !isNaN(m)) {
      const period: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
      const hours12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return { hours: String(hours12), minutes: String(m).padStart(2, '0'), period };
    }
  }
  return { hours: '', minutes: '', period: 'AM' };
}

/** Build "h:mm AM/PM" string from parts */
function formatTime(hours: number, minutes: number, period: 'AM' | 'PM'): string {
  return `${hours}:${String(minutes).padStart(2, '0')} ${period}`;
}

export const TimeInput12Hour: React.FC<TimeInput12HourProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  style = {},
}) => {
  const { hours, minutes, period } = parseParts(value);

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const h = parseInt(e.target.value, 10);
    const m = minutes ? parseInt(minutes, 10) : 0;
    onChange(formatTime(h, m, period));
  };

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const m = parseInt(e.target.value, 10);
    const h = hours ? parseInt(hours, 10) : 12;
    onChange(formatTime(h, m, period));
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = e.target.value as 'AM' | 'PM';
    const h = hours ? parseInt(hours, 10) : 12;
    const m = minutes ? parseInt(minutes, 10) : 0;
    onChange(formatTime(h, m, newPeriod));
  };

  const selectStyle: React.CSSProperties = {
    padding: '8px 4px',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: disabled ? '#f9fafb' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };

  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', ...style }}>
      <select
        value={hours}
        onChange={handleHourChange}
        disabled={disabled}
        required={required}
        style={{ ...selectStyle, width: '60px' }}
      >
        <option value="">--</option>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={String(h)}>{h}</option>
        ))}
      </select>
      <span style={{ fontWeight: 'bold' }}>:</span>
      <select
        value={minutes}
        onChange={handleMinuteChange}
        disabled={disabled}
        style={{ ...selectStyle, width: '60px' }}
      >
        <option value="">--</option>
        {Array.from({ length: 60 }, (_, i) => i).map((m) => (
          <option key={m} value={String(m).padStart(2, '0')}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      <select
        value={period}
        onChange={handlePeriodChange}
        disabled={disabled}
        style={{ ...selectStyle, width: '64px' }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};
